import { actionTypes as dataFetchActionTypes } from '@buffer-mono/async-data-fetch'
import type { Middleware } from '@reduxjs/toolkit'

import { actionTypes as orgActionTypes } from '~publish/legacy/organizations'
import type { RootState } from '~publish/legacy/store'
import type { DisconnectedProfile } from '~publish/legacy/profiles-disconnected-modal/reducer'
import { actions } from './reducer'

const PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY =
  'profilesDisconnectedModalDismissed'

type ModalMiddlewareAction = {
  type: string
  queryParams?: URLSearchParams
  result?: Array<Omit<DisconnectedProfile, 'reconnecting'>>
}

const isModalMiddlewareAction = (
  action: unknown,
): action is ModalMiddlewareAction =>
  typeof action === 'object' &&
  action !== null &&
  'type' in action &&
  typeof action.type === 'string'

const middleware: Middleware<object, RootState> =
  ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    next(action)
    if (!isModalMiddlewareAction(action)) {
      return
    }

    switch (action.type) {
      case 'APP_INIT': {
        let modal: { key: string; value?: string } | null = null
        const { queryParams: query } = action
        if (!query) {
          break
        }
        const modalKey = query.get('mk')
        const modalValue = query.get('mv')
        if (modalKey) {
          modal = { key: modalKey }
          if (modalValue) {
            modal.value = modalValue
          }
        }
        window._showModal = modal
        break
      }
      case `profiles_${dataFetchActionTypes.FETCH_SUCCESS}`: {
        if (
          action.result &&
          action.result.some(
            (profile: { isDisconnected: boolean; disabled: boolean }) =>
              profile.isDisconnected && !profile.disabled,
          )
        ) {
          // Check if user has already dismissed the modal in this session
          const isDismissed =
            sessionStorage.getItem(PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY) ===
            'true'

          if (isDismissed) {
            break // Don't show modal if already dismissed in this session
          }

          // Don't show modal while a channel connection/refresh flow is active in app-shell
          const orchestratorState =
            window.appshell?.orchestratorStore?.getState()
          const channelConnections = orchestratorState?.channelConnections
          if (
            channelConnections?.startConnection ||
            channelConnections?.isRefreshingChannelConnection
          ) {
            break
          }

          // Check if user is admin and has disconnected profiles in their organization
          const state = getState()
          // Handle cases where selected org might be null but there's a selected org in the list
          let currentOrganization = state.organizations?.selected
          if (!currentOrganization && state.organizations?.list?.length > 0) {
            // Find the organization marked as selected in the list
            currentOrganization =
              state.organizations.list.find(
                (organization) => organization.selected,
              ) || state.organizations.list[0]
          }
          const currentOrganizationId = currentOrganization?.id
          // Only show modal to users who can reconnect channels
          if (currentOrganization?.canManageChannels) {
            const disconnectedProfilesInOrg = action.result.filter(
              (profile: {
                isDisconnected: boolean
                disabled: boolean
                organizationId: string
              }) =>
                profile.isDisconnected &&
                !profile.disabled &&
                (currentOrganizationId
                  ? profile.organizationId === currentOrganizationId
                  : true),
            )

            // Only dispatch if there are disconnected profiles for this organization
            if (disconnectedProfilesInOrg.length > 0) {
              dispatch(actions.showProfilesDisconnectedModal())
            }
          }
        } else {
          // No disconnected profiles at all - clear the dismissed status
          sessionStorage.removeItem(PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY)
        }
        break
      }

      case orgActionTypes.ORGANIZATION_SELECTED: {
        // Clear dismissed status when switching organizations
        sessionStorage.removeItem(PROFILE_DISCONNECTED_MODAL_DISMISSED_KEY)
        break
      }

      default:
        break
    }
  }

export default middleware
